// The fan language switch (#1117, #1063). Shown only when the league offers
// two or more languages (leagues.enabled_locales on /public/tenant): English
// alone, or one language, shows nothing. A pick is remembered on this device
// (per host) and switches the site in place; a `?lang=` in the address is
// dropped so it can't pull the site back to the linked language.

import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLeague } from '@/contexts/LeagueContext'
import { LOCALE_NAMES, isLocale, setDeviceLocale, setLocale, useLocale, useT, type Locale } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'

export interface SiteLanguageSwitch {
  /** Two or more languages on: render the switch. */
  visible: boolean
  /** "Language", in the active language. */
  label: string
  value: Locale
  /** Each language in its own words ("Français"); render `lang={value}` on each. */
  options: Array<{ value: Locale; label: string }>
  set: (locale: Locale) => void
}

export function useLanguageSwitch(): SiteLanguageSwitch {
  const { league } = useLeague()
  const locale = useLocale()
  const label = useT(siteCoreMessages).footer.language
  const location = useLocation()
  const navigate = useNavigate()
  const enabled = (league?.enabled_locales ?? []).filter(isLocale)

  const set = useCallback(
    (next: Locale) => {
      const params = new URLSearchParams(location.search)
      if (params.has('lang')) {
        params.delete('lang')
        const search = params.toString()
        navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true, state: location.state })
      }
      setDeviceLocale(next)
      void setLocale(next)
    },
    [location, navigate]
  )

  return {
    visible: enabled.length >= 2,
    label,
    value: isLocale(locale) ? locale : enabled[0] ?? 'en',
    options: enabled.map((l) => ({ value: l, label: LOCALE_NAMES[l] })),
    set,
  }
}
